import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import cx from 'classnames';
import { Button } from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/withStyles';

import messages from '../../../locale/jsonMessages';
import { SETTINGS_LIMITS, DEFAULT_OPTION_SETTINGS } from '../../../helpers/bookingOptionsBilling';
import { fetchBookingOptionSettings, saveBookingOptionSettings } from '../../../actions/Listing/listingOptions';
import { demoRestriction } from '../../../helpers/DemoRestriction/demoRestriction';
import cs from '../../commonStyle.css';
import s from './BookingOptionSettings.css';

// Parses the text of the numeric field: only a whole number from 0 to the technical ceiling is valid.
export const parseMaxCustom = (text) => {
  const trimmed = String(text).trim();
  if (!/^[0-9]{1,3}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= SETTINGS_LIMITS.maxCustomOptionsCeiling ? value : null;
};

function Switch({ id, checked, onChange, disabled }) {
  return (
    <label className={s.switch} htmlFor={id}>
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className={s.switchTrack} aria-hidden="true" />
    </label>
  );
}

// Admin page "Options et services": limit of custom options per vehicle and global switches of the two fixed services.
export function BookingOptionSettings({ intl, dispatch }) {
  const { formatMessage } = intl;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maxText, setMaxText] = useState(String(DEFAULT_OPTION_SETTINGS.maxCustomOptions));
  const [delivery, setDelivery] = useState(true);
  const [chauffeur, setChauffeur] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const apply = (result) => {
    setMaxText(String(result.maxCustomOptions));
    setDelivery(Boolean(result.deliveryEnabled));
    setChauffeur(Boolean(result.chauffeurEnabled));
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const result = await dispatch(fetchBookingOptionSettings());
      if (!active) return;
      if (result && result.status === 'success') apply(result);
      else setFeedback({ type: 'error', text: (result && result.errorMessage) || formatMessage(messages.boAdminLoadFailed) });
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const parsed = parseMaxCustom(maxText);
  const invalid = parsed === null;

  const onSave = async (event) => {
    event.preventDefault();
    if (saving || invalid) return;
    const restricted = await dispatch(demoRestriction({}));
    if (restricted) return;
    setSaving(true);
    setFeedback(null);
    const result = await dispatch(saveBookingOptionSettings({ maxCustomOptions: parsed, deliveryEnabled: delivery, chauffeurEnabled: chauffeur }));
    setSaving(false);
    if (result && result.status === 'success') {
      apply(result);
      setFeedback({ type: 'success', text: formatMessage(messages.boAdminSaved) });
    } else {
      setFeedback({ type: 'error', text: (result && result.errorMessage) || formatMessage(messages.boAdminSaveFailed) });
    }
  };

  return (
    <div aria-busy={loading}>
      <h1 className="siteAdminHeading">{formatMessage(messages.boAdminTitle)}</h1>
      {feedback && (
        <div className={cx(s.banner, feedback.type === 'error' ? s.bannerError : s.bannerSuccess)} role={feedback.type === 'error' ? 'alert' : 'status'}>
          {feedback.text}
        </div>
      )}
      {!loading && (
        <form onSubmit={onSave} noValidate>
          <section className={s.panel}>
            <h2 className={s.panelTitle}>{formatMessage(messages.boAdminMaxTitle)}</h2>
            <div className={s.panelBody}>
              <div className={s.field}>
                <label className={s.label} htmlFor="bo-admin-max">{formatMessage(messages.boAdminMaxLabel)}</label>
                <input id="bo-admin-max" type="number" inputMode="numeric" min={0} max={SETTINGS_LIMITS.maxCustomOptionsCeiling} step={1}
                  className="form-control commonInputField" value={maxText} onChange={(e) => setMaxText(e.target.value)} aria-invalid={invalid} />
                {invalid && <p className={s.errorText} role="alert">{formatMessage(messages.boAdminMaxInvalid, { max: SETTINGS_LIMITS.maxCustomOptionsCeiling })}</p>}
              </div>
              <p className={s.help}>{formatMessage(messages.boAdminMaxHelp, { max: SETTINGS_LIMITS.maxCustomOptionsCeiling })}</p>
            </div>
          </section>

          <section className={s.panel}>
            <h2 className={s.panelTitle}>{formatMessage(messages.boAdminServicesTitle)}</h2>
            <div className={s.panelBody}>
              <div className={s.serviceRow}>
                <span className={s.serviceName}>{formatMessage(messages.boDeliveryLabel)}</span>
                <Switch id="bo-admin-delivery" checked={delivery} onChange={setDelivery} disabled={saving} />
              </div>
              <div className={s.serviceRow}>
                <span className={s.serviceName}>{formatMessage(messages.boChauffeurLabel)}</span>
                <Switch id="bo-admin-chauffeur" checked={chauffeur} onChange={setChauffeur} disabled={saving} />
              </div>
              <p className={s.help}>{formatMessage(messages.boAdminServicesHelp)}</p>
              <p className={s.help}>{formatMessage(messages.boAdminTextsHelp)}</p>
            </div>
          </section>

          <div className={s.actions}>
            <Button className={cx(cs.btnPrimary)} type="submit" disabled={saving || invalid}>{formatMessage(messages.save)}</Button>
          </div>
        </form>
      )}
    </div>
  );
}

BookingOptionSettings.propTypes = {
  title: PropTypes.string,
};

export default injectIntl(withStyles(s)(connect()(BookingOptionSettings)));
